import { Atom } from 'lucide-react';
import { PHYSICS_DOMAINS } from './physicsDomains/domains';
import { PHYSICS_DOMAINS_ENABLED } from './physicsDomains/domainTypes';
import {
  Activity,
  BarChart3,
  Calculator,
  ChartSpline,
  Move,
  Mountain,
  MoveUpRight,
  Radiation,
  Rainbow,
  Scale,
  Shapes,
  Sigma,
  Target,
  TrendingUp,
  Triangle,
  WalletCards,
  Waves,
  Zap,
} from 'lucide-react';

import { CHEMISTRY_STUDY_TOOLS } from './chemistryStudyTools';
import { STUDY_SUBJECTS } from './studyCatalog';
import { STUDY_TOOL_CATEGORIES } from './studyToolCategories';
import { BIOLOGY_TOOLS } from './studyToolsBiology';
import { EARTH_ENERGY_BUDGET_TOOLS, EARTH_SCIENCE_SUITE_TOOLS } from './studyToolsEarth';
import {
  toolVisibleFromYear,
  type StudyTool,
  type StudyToolCategory,
  type StudyToolTone,
} from './studyToolTypes';

export type { StudyTool, StudyToolCategory, StudyToolTone } from './studyToolTypes';
export { toolVisibleFromYear } from './studyToolTypes';
export { STUDY_TOOL_CATEGORIES } from './studyToolCategories';

/**
 * Registry of standalone interactive study tools per Subject Space. A tool is
 * open to any student with the subject, needs no School/Year/State setup, does
 * not update mastery and does not claim alignment with a state syllabus
 * (airbotix-academy-ai-study-workspace-prd §6.11).
 */

export const STUDY_TOOLS: StudyTool[] = [
  {
    id: 'fraction-ratio-lab',
    toolVersion: '1.1.0',
    subjectKey: 'mathematics',
    category: 'number-finance',
    name: 'Fractions, Percent & Ratio Lab',
    description:
      'Enter a fraction, split a total in a ratio or find a percentage amount, with linked diagrams and complete worked steps.',
    path: '/learn/study/subjects/mathematics/tools/fractions-ratios',
    actionLabel: 'Open fractions and ratio lab',
    icon: Calculator,
    tone: 'sunshine',
  },
  {
    id: 'algebra-machine',
    toolVersion: '1.3.0',
    subjectKey: 'mathematics',
    category: 'algebra-functions',
    name: 'Algebra Balance & Function Machine',
    description: 'Enter ax + b = c directly. Follow inverse operations, exact substitution and a linked intersection graph.',
    path: '/learn/study/subjects/mathematics/tools/algebra-machine',
    actionLabel: 'Open algebra machine',
    icon: Scale,
    tone: 'mint',
  },
  {
    id: 'simultaneous-equations',
    toolVersion: '1.2.0',
    subjectKey: 'mathematics',
    category: 'algebra-functions',
    name: 'Simultaneous Equations Lab',
    description: 'Enter two to five equations. Follow elimination or substitution and check every equation against the graph.',
    path: '/learn/study/subjects/mathematics/tools/simultaneous-equations',
    actionLabel: 'Open simultaneous equations',
    icon: Scale,
    tone: 'sky',
    availableFrom: 10,
  },
  {
    id: 'functions-graphs',
    toolVersion: '1.6.0',
    subjectKey: 'mathematics',
    category: 'algebra-functions',
    name: 'Functions & Graphs Explorer',
    description: 'Enter coefficients and x to connect function values, graphs, intercepts and full worked steps.',
    path: '/learn/study/subjects/mathematics/tools/functions-graphs',
    actionLabel: 'Open functions and graphs',
    icon: ChartSpline,
    tone: 'sky',
  },
  {
    id: 'quadratic-solver',
    toolVersion: '1.2.0',
    subjectKey: 'mathematics',
    category: 'algebra-functions',
    name: 'Quadratic Roots & Discriminant',
    description: 'Enter a, b and c to see the graph, substituted quadratic formula, discriminant, roots and root checks.',
    path: '/learn/study/subjects/mathematics/tools/quadratic-solver',
    actionLabel: 'Open quadratic solver',
    icon: Calculator,
    tone: 'coral',
    availableFrom: 10,
  },
  {
    id: 'geometry-measurement',
    toolVersion: '1.5.0',
    subjectKey: 'mathematics',
    category: 'geometry-coordinate',
    name: 'Geometry & Measurement Studio',
    description:
      'Enter dimensions to update 2D and 3D shapes, complete worked steps, surface area and volume together.',
    path: '/learn/study/subjects/mathematics/tools/geometry-measurement',
    actionLabel: 'Open geometry studio',
    icon: Shapes,
    tone: 'coral',
  },
  {
    id: 'coordinate-transformations',
    toolVersion: '1.1.0',
    subjectKey: 'mathematics',
    category: 'geometry-coordinate',
    name: 'Coordinate Transformations',
    description: 'Predict translations, reflections, rotations and scale changes on numbered axes.',
    path: '/learn/study/subjects/mathematics/tools/coordinate-transformations',
    actionLabel: 'Open coordinate transformations',
    icon: Move,
    tone: 'sky',
  },
  {
    id: 'coordinate-distance',
    toolVersion: '1.1.0',
    subjectKey: 'mathematics',
    category: 'geometry-coordinate',
    name: 'Coordinate Distance Explorer',
    description:
      'Build the right triangle between two points and calculate straight-line distance.',
    path: '/learn/study/subjects/mathematics/tools/coordinate-distance',
    actionLabel: 'Open coordinate distance',
    icon: Move,
    tone: 'mint',
    availableFrom: 9,
  },
  {
    id: 'angles-trigonometry',
    toolVersion: '1.2.0',
    subjectKey: 'mathematics',
    category: 'geometry-coordinate',
    name: 'Angles & Trigonometry Lab',
    description:
      'Enter sides and angles; see a matching triangle, live results and step-by-step Pythagoras, tangent or cosine-rule working.',
    path: '/learn/study/subjects/mathematics/tools/angles-trigonometry',
    actionLabel: 'Open angles and trigonometry',
    icon: Triangle,
    tone: 'sunshine',
  },
  {
    id: 'sequences-growth',
    toolVersion: '1.2.0',
    subjectKey: 'mathematics',
    category: 'patterns-change',
    name: 'Sequences & Growth',
    description: 'Enter a first term, difference or ratio and target position. See signed sequence points and complete worked steps.',
    path: '/learn/study/subjects/mathematics/tools/sequences-growth',
    actionLabel: 'Open sequences and growth',
    icon: TrendingUp,
    tone: 'mint',
  },
  {
    id: 'logarithm-explorer',
    toolVersion: '1.2.0',
    subjectKey: 'mathematics',
    category: 'patterns-change',
    name: 'Powers & Logarithms',
    description: 'Enter a base and positive value. Explore linked power/logarithm curves, an adaptive power table and worked steps.',
    path: '/learn/study/subjects/mathematics/tools/logarithm-explorer',
    actionLabel: 'Open logarithm explorer',
    icon: Sigma,
    tone: 'sunshine',
    availableFrom: 11,
  },
  {
    id: 'indices-powers',
    toolVersion: '1.2.0',
    subjectKey: 'mathematics',
    category: 'number-finance',
    name: 'Indices & Powers',
    description: 'Enter a numeric base and exponents. See the common-base law, power curve and calculated result together.',
    path: '/learn/study/subjects/mathematics/tools/indices-powers',
    actionLabel: 'Open indices and powers',
    icon: Sigma,
    tone: 'sunshine',
    availableFrom: 8,
  },
  {
    id: 'money-models',
    toolVersion: '1.1.0',
    subjectKey: 'mathematics',
    category: 'number-finance',
    name: 'Money Models',
    description: 'Compare discounts, simple interest and repeated compound growth.',
    path: '/learn/study/subjects/mathematics/tools/money-models',
    actionLabel: 'Open money models',
    icon: WalletCards,
    tone: 'coral',
  },
  {
    id: 'rates-calculus',
    toolVersion: '1.3.0',
    subjectKey: 'mathematics',
    category: 'patterns-change',
    name: 'Rates of Change',
    description:
      'Enter a function to inspect its tangent and signed integral, or practise the power rule.',
    path: '/learn/study/subjects/mathematics/tools/rates-calculus',
    actionLabel: 'Open rates of change',
    icon: Activity,
    tone: 'sky',
    availableFrom: 11,
  },
  {
    id: 'data-probability',
    toolVersion: '1.6.0',
    subjectKey: 'mathematics',
    category: 'data-chance',
    name: 'Data & Probability Lab',
    description:
      'Explore data, adjustable distributions and probability trees with repeatable simulations.',
    path: '/learn/study/subjects/mathematics/tools/data-probability',
    actionLabel: 'Open data and probability lab',
    icon: BarChart3,
    tone: 'mint',
  },
  ...CHEMISTRY_STUDY_TOOLS,
  ...(PHYSICS_DOMAINS_ENABLED
    ? PHYSICS_DOMAINS.map(
        (domain): StudyTool => ({
          id: domain.id,
          subjectKey: 'physics',
          category: 'senior-experiments',
          name: domain.name,
          description: domain.hint,
          path: `/learn/study/subjects/physics/tools/${domain.id}`,
          actionLabel: 'Open experiment preview',
          icon: Atom,
          tone: 'sky',
        }),
      )
    : []),
  {
    id: 'physics-lab',
    toolVersion: '1.0.0',
    subjectKey: 'physics',
    category: 'toolkit',
    name: 'Physics Lab',
    description:
      'Open a complete, editable circuit or explore gravity, springs and motion in a full-screen lab. Prepared model examples for free exploration.',
    path: '/learn/study/subjects/physics/tools/physics-lab',
    actionLabel: 'Open Physics Lab',
    icon: Atom,
    tone: 'sunshine',
    general: true,
    keywords: 'physics laboratory circuit mechanics spring gravity force 物理 实验室 电路 力学 弹簧 重力',
  },
  {
    id: 'formulas',
    toolVersion: '2.2.0',
    subjectKey: 'physics',
    category: 'toolkit',
    name: 'Formulas & Units',
    description:
      'Find any unknown with full working, change units and look up constants with significant figures handled for you.',
    path: '/learn/study/subjects/physics/tools/formulas',
    actionLabel: 'Open formulas',
    icon: Sigma,
    tone: 'coral',
  },
  {
    id: 'motion-graphs',
    subjectKey: 'physics',
    category: 'motion-forces',
    name: 'Motion Graph Lab',
    description:
      'Build a journey and watch the position, velocity and acceleration graphs change together.',
    path: '/learn/study/subjects/physics/tools/motion-graphs',
    actionLabel: 'Open motion graphs',
    icon: ChartSpline,
    tone: 'sky',
  },
  {
    id: 'vectors-forces',
    toolVersion: '1.1.0',
    subjectKey: 'physics',
    category: 'motion-forces',
    name: 'Vectors & Forces',
    description:
      'Drag force arrows, split them into components and see the net force and the acceleration it causes.',
    path: '/learn/study/subjects/physics/tools/vectors-forces',
    actionLabel: 'Open vectors and forces',
    icon: MoveUpRight,
    tone: 'mint',
  },
  {
    id: 'projectile',
    subjectKey: 'physics',
    category: 'motion-forces',
    name: 'Projectile Motion',
    description:
      'Kick or throw a ball at any speed, angle and height, on Earth, Mars or the Moon. See why sideways speed stays the same while gravity changes the rest.',
    path: '/learn/study/subjects/physics/tools/projectile',
    actionLabel: 'Open projectile motion',
    icon: Target,
    tone: 'sunshine',
  },
  {
    id: 'light',
    toolVersion: '1.2.0',
    subjectKey: 'physics',
    category: 'waves',
    name: 'Light & Refraction',
    description:
      "Shine a ray from air into water, glass or diamond and back. See it bend by Snell's law, and find the critical angle where no light gets out.",
    path: '/learn/study/subjects/physics/tools/light',
    actionLabel: 'Open light and refraction',
    icon: Rainbow,
    tone: 'coral',
  },
  {
    id: 'half-life',
    subjectKey: 'physics',
    category: 'atoms',
    name: 'Half-life',
    description:
      'Watch hundreds of unstable atoms decay at random and compare the count with the half-life rule. Carbon dating and medical isotopes included.',
    path: '/learn/study/subjects/physics/tools/half-life',
    actionLabel: 'Open half-life',
    icon: Radiation,
    tone: 'mint',
  },
  {
    id: 'circuits',
    toolVersion: '1.2.0',
    subjectKey: 'physics',
    category: 'electricity',
    name: 'Series & Parallel Circuits',
    description:
      'Build your own wired circuit or explore series, parallel and mixed examples. Measure current and voltage.',
    path: '/learn/study/subjects/physics/tools/circuits',
    actionLabel: 'Open circuits',
    icon: Zap,
    tone: 'sunshine',
  },
  {
    id: 'waves',
    toolVersion: '1.1.0',
    subjectKey: 'physics',
    category: 'waves',
    name: 'Waves Lab',
    description:
      'Shake a rope, push a slinky or send a sound. See how frequency, wavelength and speed fit together in v = fλ.',
    path: '/learn/study/subjects/physics/tools/waves',
    actionLabel: 'Open waves',
    icon: Waves,
    tone: 'sky',
  },
  {
    id: 'energy',
    subjectKey: 'physics',
    category: 'energy',
    name: 'Energy Transfer',
    description:
      'Send a skater down a half-pipe and watch height energy, moving energy and heat change while the total stays the same.',
    path: '/learn/study/subjects/physics/tools/energy',
    actionLabel: 'Open energy',
    icon: Mountain,
    tone: 'coral',
  },
  ...BIOLOGY_TOOLS,
  ...EARTH_ENERGY_BUDGET_TOOLS,
  ...EARTH_SCIENCE_SUITE_TOOLS,
];

/** "1 tool", "3 tools", "1 topic", "6 topics". */
export function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

export function toolsForSubject(subjectKey: string | undefined): StudyTool[] {
  return STUDY_TOOLS.filter((tool) => tool.subjectKey === subjectKey);
}

export function toolsForYear(subjectKey: string | undefined, year: number | null): StudyTool[] {
  return toolsForSubject(subjectKey).filter(
    (tool) => year === null || year >= toolVisibleFromYear(tool),
  );
}

/** General tools of one subject, in catalogue order. */
export function generalToolsForSubject(subjectKey: string | undefined): StudyTool[] {
  return toolsForSubject(subjectKey).filter((tool) => tool.general);
}

const MATHEMATICS_RECOMMENDATIONS: Record<number, readonly string[]> = {
  6: ['fraction-ratio-lab', 'geometry-measurement'],
  7: ['fraction-ratio-lab', 'algebra-machine'],
  8: ['indices-powers', 'functions-graphs'],
  9: ['coordinate-distance', 'data-probability'],
  10: ['simultaneous-equations', 'quadratic-solver'],
  11: ['logarithm-explorer', 'rates-calculus'],
  12: ['functions-graphs', 'rates-calculus'],
};

export function recommendedToolsForYear(subjectKey: string, year: number | null): StudyTool[] {
  if (subjectKey !== 'mathematics' || year === null) return [];
  const ids = MATHEMATICS_RECOMMENDATIONS[year] ?? [];
  return ids.flatMap((id) => {
    const tool = STUDY_TOOLS.find((item) => item.id === id);
    return tool ? [tool] : [];
  });
}

export interface ToolGroup {
  category: StudyToolCategory;
  tools: StudyTool[];
}

/** The tools of one subject, grouped by category in the order categories are listed. */
export function toolGroupsForSubject(
  subjectKey: string | undefined,
  year: number | null = null,
): ToolGroup[] {
  const visibleTools = toolsForYear(subjectKey, year);
  return STUDY_TOOL_CATEGORIES.filter((category) => category.subjectKey === subjectKey)
    .map((category) => ({
      category,
      tools: visibleTools.filter((tool) => tool.category === category.id),
    }))
    .filter((group) => group.tools.length > 0);
}

/** Subjects that have at least one tool, in the order their tools are listed. */
export function subjectsWithTools(): string[] {
  return [...new Set(STUDY_TOOLS.map((tool) => tool.subjectKey))];
}

export function categoryOf(tool: StudyTool): StudyToolCategory | undefined {
  return STUDY_TOOL_CATEGORIES.find(
    (category) => category.id === tool.category && category.subjectKey === tool.subjectKey,
  );
}

export function toolsHubPath(subjectKey: string): string {
  return `/learn/study/subjects/${subjectKey}/tools`;
}

export function subjectLabel(subjectKey: string): string {
  return STUDY_SUBJECTS.find(([key]) => key === subjectKey)?.[1] ?? subjectKey;
}

export const STUDY_TOOL_TONE_CLASSES: Record<StudyToolTone, { card: string; accent: string }> = {
  sky: { card: 'border-brand-sky/25 bg-wash-sky', accent: 'text-brand-sky' },
  coral: { card: 'border-brand-coral/25 bg-wash-coral', accent: 'text-brand-coral' },
  mint: { card: 'border-brand-mint/30 bg-wash-mint', accent: 'text-brand-mint' },
  sunshine: { card: 'border-brand-sunshine/45 bg-wash-sunshine', accent: 'text-ink' },
};
